import React, { useEffect, useState } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import {
  fetchFailureData,
  removeFailureData,
} from '../../../../modules/executions';
import SkeletonLoader from '../../../elements/SkeletonLoader';
import AddEditFailure from './AddEditFailure';
import ConfirmationModal from '../../../../ui/primitives/ConfirmationModal';
import { useNotification } from '../../../../hooks/NotificationContext';
import { isExecutiveManager } from '../../../../lib/userUtils';
import { WarningIcon } from '../../../elements/CustomIcons';
import DetailsCard from '../../../../ui/layout/DetailsCard';
import ActionMenu from '../../../../ui/primitives/ActionMenu';
import { IconButton, Tooltip } from '@mui/material';
import { Guard } from '../../../../features/Guard';

const FailureItem = ({ item, onEdit, onDelete }) => (
  <Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr) auto',
        gap: 2,
        mb: 2,
        alignItems: 'flex-start',
      }}
    >
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Category
        </Typography>
        <Typography variant="body2">{item.category}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Sub Category
        </Typography>
        <Typography variant="body2">{item.sub_category}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Added By
        </Typography>
        <Typography variant="body2">{item.added_by}</Typography>
      </Box>
      <Box>
        <Guard policy="ManageTestExecutions">
        <ActionMenu
          tooltip="Item actions"
          items={[
            {
              label: 'Edit',
              icon: <Edit />,
              onClick: () => onEdit(item),
            },
            {
              label: 'Delete',
              icon: <Delete />,
              color: 'error',
              onClick: () => onDelete(item),
            },
          ]}
        />
        </Guard>
      </Box>
    </Box>

    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        Description
      </Typography>
      <Typography variant="body2">
        {item.description || 'No details added'}
      </Typography>
    </Box>
  </Box>
);

const FailuresAnalysis = ({ executionId, testCaseId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addNotification } = useNotification();
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [mode, setMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchFailureData(executionId);
      setData(response.failures);
    } catch (error) {
      addNotification('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOpen = (type, item) => {
    setShowAddPopup(true);
    setMode(type);
    setSelectedItem(item);
  };

  const handleClose = () => {
    setShowAddPopup(false);
    setSelectedItem(null);
  };

  const handleOpenDeleteConfirm = (item) => {
    setOpenDeleteConfirm(true);
    setSelectedItem(item);
  };

  const handleCloseConfirm = () => {
    setOpenDeleteConfirm(false);
    setSelectedItem(null);
  };

  const handleDelete = async () => {
    try {
      await removeFailureData(selectedItem?.id);
      fetchData();
      handleCloseConfirm();
      addNotification('success', 'Item removed successfully');
    } catch (error) {
      addNotification('error', error.message);
    }
  };

  useEffect(() => {
    if (executionId) fetchData();
    return () => setData([]);
  }, [executionId]);

  const showWarning = data?.length === 0;
  const canAdd = !isExecutiveManager() && data?.length === 0;

  const headerActions = canAdd ? (
    <Guard policy="ManageTestExecutions">
    <Tooltip title="Add New Analysis">
      <IconButton
        color="primary"
        onClick={() => handleAddOpen('add', null)}
        aria-label="Add New Analysis"
      >
        <Add />
      </IconButton>
    </Tooltip>
    </Guard>
  ) : null;

  const titleNode = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="h6">Failure Analysis</Typography>
      {showWarning && (
        <WarningIcon tooltipText="Add a failure analysis to this failed execution." />
      )}
    </Box>
  );

  return (
    <>
      <DetailsCard title={titleNode} actions={headerActions}>
        {loading ? (
          <SkeletonLoader rowCount={2} />
        ) : data.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No data added yet
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />} spacing={3}>
            {data.map((item, index) => (
              <FailureItem
                key={item.id || index}
                item={item}
                onEdit={(item) => handleAddOpen('edit', item)}
                onDelete={handleOpenDeleteConfirm}
              />
            ))}
          </Stack>
        )}
      </DetailsCard>

      <AddEditFailure
        show={showAddPopup}
        handleClose={handleClose}
        mode={mode}
        fetchData={fetchData}
        selectedItem={selectedItem}
        testCaseId={testCaseId}
        executionId={executionId}
      />

      <ConfirmationModal
        show={openDeleteConfirm}
        handleClose={handleCloseConfirm}
        handleConfirm={handleDelete}
        body="Are you sure you want to remove this item?"
      />
    </>
  );
};

export default FailuresAnalysis;
